/**
 * Build the OTel Resource for the SDK process — set ONCE at provider
 * construction, attached to every signal (logs/metrics/traces).
 *
 * Naming conventions match `backend/TELEMETRY.md`:
 *   service.name           = morfi-sdk-<platform>-<env>
 *   service.version        = <SDK package.json#version>
 *   service.namespace      = morfi
 *   deployment.environment = devnet | mainnet
 *
 * Platform discriminator lives in `morfi.platform` (web | react-native) so
 * Better Stack dashboards filter by it natively. OS and runtime are best-
 * effort — host apps can override via the `platform` field of TelemetryConfig.
 */

import { attrs, type OtlpKeyValue, type OtlpResource } from './otel-types.js';

/** Service-name kind component. */
export type ServicePlatform = 'web' | 'react-native';

/** Hand-curated kebab-case mobile-platform label (Apple-style consistency). */
export type ServicePlatformOs = 'browser' | 'ios' | 'android' | 'unknown';

/** Best-effort runtime label — chrome/firefox/safari/edge/hermes/jsc/node/unknown. */
export type ServicePlatformRuntime = string;

export interface ResourceConfig {
  readonly app: {
    readonly name: string;
    readonly version: string;
    readonly build?: string;
  };
  readonly network: 'devnet' | 'mainnet';
  readonly sdkVersion: string;
  /**
   * Host-supplied overrides. When omitted, the SDK auto-detects via
   * navigator.product + userAgent. RN apps that know their platform.os
   * exactly should pass it through here.
   */
  readonly platform?: {
    readonly kind?: ServicePlatform;
    readonly os?: ServicePlatformOs;
    readonly runtime?: ServicePlatformRuntime;
  };
}

/** Auto-detect platform. */
export function detectPlatform(): ServicePlatform {
  // RN sets navigator.product = 'ReactNative' per the React Native runtime
  // conventions. The check is structural so jsdom (which has no navigator
  // at all unless set up) defaults to 'web'.
  const nav = (globalThis as { navigator?: { product?: string } }).navigator;
  if (nav?.product === 'ReactNative') return 'react-native';
  return 'web';
}

/** Auto-detect OS — coarse. */
export function detectOs(): ServicePlatformOs {
  const platform = detectPlatform();
  if (platform === 'web') return 'browser';
  const ua = (globalThis as { navigator?: { userAgent?: string } }).navigator?.userAgent ?? '';
  // RN sets userAgent to "okhttp/x.x.x" on Android and "CFNetwork ..." on iOS,
  // but only for fetch — the platform module is the authoritative source.
  // Host apps should pass platform.os explicitly; this is a fallback only.
  if (/CFNetwork|Darwin|iPhone|iPad|iOS/i.test(ua)) return 'ios';
  if (/Android|Linux.*Android|okhttp/i.test(ua)) return 'android';
  return 'unknown';
}

/** Auto-detect JS engine runtime. */
export function detectRuntime(): ServicePlatformRuntime {
  // Hermes is the default RN engine since RN 0.70.
  if ('HermesInternal' in (globalThis as object)) return 'hermes';
  // V8 in Chrome/Edge/Node. JSC in Safari/older RN.
  const ua = (globalThis as { navigator?: { userAgent?: string } }).navigator?.userAgent ?? '';
  const m = /(Chrome|Edg|Firefox|Safari)\/([\d.]+)/i.exec(ua);
  if (m !== null && m[1] !== undefined && m[2] !== undefined) {
    return `${m[1].toLowerCase()} ${m[2]}`;
  }
  if (typeof process !== 'undefined' && process.versions?.node !== undefined) {
    return `node ${process.versions.node}`;
  }
  return 'unknown';
}

/**
 * Build the OTel resource. Service name follows backend convention:
 * `morfi-sdk-web-devnet` / `morfi-sdk-mobile-mainnet`. Web platform tag
 * maps to `web`; RN platform tag maps to `mobile` in the service-name
 * because that's the name backend dashboards expect.
 */
export function buildResourceAttributes(config: ResourceConfig): ReadonlyArray<OtlpKeyValue> {
  const platform = config.platform?.kind ?? detectPlatform();
  const os = config.platform?.os ?? detectOs();
  const runtime = config.platform?.runtime ?? detectRuntime();
  const platformLabel = platform === 'react-native' ? 'mobile' : 'web';
  return attrs({
    'service.name': `morfi-sdk-${platformLabel}-${config.network}`,
    'service.version': config.sdkVersion,
    'service.namespace': 'morfi',
    'deployment.environment': config.network,
    'telemetry.sdk.name': 'morfi-backend-sdk-v2',
    'telemetry.sdk.language': 'webjs',
    'telemetry.sdk.version': config.sdkVersion,
    'morfi.platform': platform,
    'morfi.platform.os': os,
    'morfi.platform.runtime': runtime,
    'morfi.app.name': config.app.name,
    'morfi.app.version': config.app.version,
    'morfi.app.build': config.app.build,
    'morfi.network': config.network,
  });
}

/** Convenience: assemble the full Resource for the exporter. */
export function buildResource(config: ResourceConfig): OtlpResource {
  return { attributes: buildResourceAttributes(config) };
}
